import { useFormik } from "formik"
import * as Yup from "yup"
import { useDispatch, useSelector } from "react-redux"
import validationError from "../../../../enums/validationError"
import { setPOCFormModule } from "../../RaiseTicketFormSlice"
import { TStore } from "../../../../store/Store"
import { useAddSurveyTicketMutation } from "../../../../pages/Operations/features/Survey/SurveyService"
import { uploadFilesToS3 } from "../../../../utils/utils"
import { setAlertFunc } from "../../../AlertToaster/AlertSlice"
import { DropdownObj } from "../../../../pages/Common/commonTypes"
import { useAddDemolitionTicketMutation } from "../../../../pages/Operations/features/Demolition/DemolitionService"
import { EFormModule } from "../.."
import { EAccountRole } from "../../../../enums/commonEnums"
import { useAddPaymentTermsInBulkMutation } from "../../../../pages/Finance/FinanceServices"

interface InputItem {
    type: DropdownObj | null
    rating: string
    quantity: string
}

interface OutputItem {
    type: any
    rating: string
    quantity: number
}
const usePocDetails = (handleNextStep: () => void, module: EFormModule, redirectPage?: boolean) => {
    const dispatch = useDispatch()
    const {
        poc_details_form,
        solo_survey_form,
        sites_form,
        deal_sow_form,
        demolition_charger_form,
        project_details_form,
        sites_form_demolition,
        payment_details_form,
    } = useSelector((state: TStore) => state.RaiseTicketForm)
    const { user_id } = useSelector((store: TStore) => store.verifyLogin)

    const [addDemolitionTicket, { isLoading: isAddDemolitionLoading }] = useAddDemolitionTicketMutation()
    const [addPaymentTerms, { isLoading: isAddingPaymentTerms }] = useAddPaymentTermsInBulkMutation()

    const [addSurveyTicket, { isLoading: isAddSurveyLoading }] = useAddSurveyTicketMutation()
    const aggregateItems = (items: InputItem[]): OutputItem[] => {
        const transformedItems = items.map(item => ({
            type: item?.type?.id,
            rating: parseFloat(item.rating).toFixed(2),
            quantity: parseFloat(item.quantity),
        }))

        const aggregated = transformedItems.reduce(
            (acc, item) => {
                const key = `${item.type}-${item.rating}`

                if (!acc[key]) {
                    acc[key] = { type: item?.type, rating: JSON.parse(item.rating), quantity: item.quantity }
                } else {
                    acc[key].quantity += item.quantity
                }

                return acc
            },
            {} as Record<string, OutputItem>
        )

        return Object.values(aggregated)
    }
    const formik = useFormik({
        initialValues: poc_details_form,
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
            contact_number: Yup.string()
                .matches(/^\d{10}$/, validationError.PHONE)
                .required("Contact number is required"),
            email: Yup.string().email(validationError.EMAIL).notRequired().nullable(),
        }),
        onSubmit: async values => {
            dispatch(
                setPOCFormModule({
                    name: values.name,
                    contact_number: values.contact_number,
                    email: values.email,
                })
            )
            if (module === EFormModule.SURVEYS) {
                handleSurveySubmit()
            }
            if (module === EFormModule.DEMOLITION) {
                handleDemolitionSubmit()
            }
        },
    })

    const handleAddPaymentTerms = async (ticketId: number) => {
        const formData = new FormData()
        const paymentTermsObj = payment_details_form.payment_terms_obj.map((term: any, idx: number) => {
            if (term?.condition?.id === 7) {
                if (typeof term?.evidence_file?.[0] !== "string") {
                    formData.append(`evidence_file_${idx + 1}`, term?.evidence_file?.[0])
                }
            }

            let payload: Record<string, any> = {
                pmt_type: term?.pmt_type?.id,
                pmt_percent: term?.pmt_percent,
                bank_guarantee: term?.bank_guarantee || 0,
                pmt_due_date: term?.pmt_due_date,
                condition: term?.condition?.id,
                time_qty: term?.time_qty,
                time_uom: term?.time_uom?.id,
                condition_text: term?.condition_text,
                _id: idx + 1,
            }

            if (term?.payment_term_id) {
                payload = {
                    ...payload,
                    payment_term_id: term?.payment_term_id,
                }
            }

            let conditionalPayload: Record<string, any> = {
                utr: term?.utr,
                original_amt: term?.original_amt,
                txn_due_date: term?.txn_due_date,
                paid_at: term?.paid_at,
                net_deduction: term?.net_deduction,
                remarks: term?.remarks,
            }

            if (term?.transaction_id) {
                conditionalPayload = {
                    ...conditionalPayload,
                    id: term?.transaction_id,
                }
            }

            if (term?.condition?.id === 7 || term?.condition_text === "Already paid as advance") {
                return {
                    ...payload,
                    payment_evidence: conditionalPayload,
                }
            }
            return payload
        })
        formData.append("ticket_id", ticketId.toString())
        formData.append("total_amount", payment_details_form.total_amount)
        formData.append("payment_terms", paymentTermsObj?.length ? JSON.stringify(paymentTermsObj) : "{}")
        await addPaymentTerms(formData)
    }

    const handleDemolitionSubmit = async () => {
        const values = formik.values
        const accountPayload = Object.keys(sites_form_demolition.accountFormMap).map(key => ({
            account_id: sites_form_demolition?.accountFormMap[key as EAccountRole]?.accountId?.id?.toString()!,
            account_role: key,
            billing_address_id:
                sites_form_demolition?.accountFormMap[key as EAccountRole]?.billingAddress?.id?.toString()!,
            contact_id: sites_form_demolition?.accountFormMap[key as EAccountRole]?.billingContact?.id?.toString()!,
        }))
        const formData = new FormData()
        formData.append("deal_type", deal_sow_form.deal_type?.title)
        formData.append("project_name", project_details_form.project_name)
        formData.append("source_site_id", sites_form_demolition.sourceSite?.id?.toString()!)
        formData.append("destination_site_id", sites_form_demolition.destinationSite?.id?.toString()!)
        formData.append("deal_done_by_id", project_details_form.deal_done_by?.id?.toString()!)
        formData.append("project_closure_date", project_details_form.project_closure_date)
        formData.append("reason_id", project_details_form.reason?.id?.toString()!)
        formData.append("is_station_dismantled", demolition_charger_form.is_entire_station_being_dismantled ? "1" : "0")
        formData.append("total_amount", payment_details_form.total_amount)
        formData.append("account_details", JSON.stringify(accountPayload))
        formData.append("poc_name", values.name)
        formData.append("poc_phone", values.contact_number)
        formData.append("poc_email", values.email)
        const chargerPayload = demolition_charger_form.chargers
            .filter(i => Boolean(i.charger_type_id))
            .map(charger => ({
                serial_cin: charger.serial_cin,
                name: charger.charger_name,
                charger_sku: charger.sku_code?.title || "",
                charger_id: charger.charger_type_id,
            }))
        formData.append("new_chargers", JSON.stringify(chargerPayload))
        formData.append("charger_ids", JSON.stringify(demolition_charger_form.selected_chargers))

        const purchaseOrders = project_details_form.purchase_order
        if (purchaseOrders) {
            for (let i = 0; i < purchaseOrders.length; i++) {
                formData.append("purchase_order", purchaseOrders[i])
            }
        }
        const dealDocs = project_details_form.deal_docs
        if (dealDocs) {
            for (let i = 0; i < dealDocs.length; i++) {
                formData.append("deal_docs", dealDocs[i])
            }
        }

        const res = await addDemolitionTicket(formData).unwrap()

        if (res.add_payment_terms && deal_sow_form.deal_type?.title === "Outright") {
            await handleAddPaymentTerms(res.finance_id)
            if (redirectPage) {
                window.open("/Demolition", "_blank")
            }
        }
        handleNextStep()
    }

    const handleSurveySubmit = async () => {
        const values = formik.values
        const formData = new FormData()
        formData.append("project_name", solo_survey_form?.project_name)
        formData.append("hosting_address", sites_form.siteAddress)
        formData.append("pincode", sites_form.pincode)
        formData.append("state_id", JSON.stringify(sites_form?.state?.id))
        formData.append("city_id", JSON.stringify(sites_form.city?.id))
        formData.append("business_poc_id", JSON.stringify(solo_survey_form?.business_poc?.id))
        formData.append("latitude", sites_form?.latitude)
        formData.append("longitude", sites_form?.longitude)
        formData.append("site_name", sites_form?.siteName)
        formData.append("no_of_spots", "5")
        formData.append("poc_name", values.name)
        formData.append("poc_phone", values.contact_number)
        formData.append("poc_email", values.email)
        formData.append("user_id", user_id)
        formData.append("deal_type", deal_sow_form.deal_type?.title)
        formData.append("visit_by", solo_survey_form.visit_by_date)
        formData.append("time_of_day", JSON.stringify(solo_survey_form.visit_by_time.id))
        formData.append("chargers", JSON.stringify(aggregateItems(solo_survey_form.chargers)))
        formData.append("load_available", solo_survey_form.load_available)
        formData.append("regular_demand_load", solo_survey_form.demand_load)
        formData.append("sanctioned_load", solo_survey_form.sanctioned_load)
        formData.append("foundation_type", solo_survey_form.foundation_type?.title?.toUpperCase())
        formData.append("electricity_connection_type", solo_survey_form.connection_type?.title?.toUpperCase())
        formData.append("site_id", JSON.stringify(sites_form.siteId))

        let res = await addSurveyTicket(formData)
        handleNextStep()
        if ("data" in res) {
            try {
                const ticketId = JSON.stringify(res.data.data.ticket_id)

                const payload = {
                    survey_spot_images: Array.from(solo_survey_form?.spot_image),
                    electricity_bill_survey: Array.from(solo_survey_form?.electricity_bill),
                }
                await uploadFilesToS3(payload, ticketId, true)
                if (redirectPage) {
                    window.open("/Survey", "_blank")
                }
            } catch (error: any) {
                dispatch(setAlertFunc({ type: "error", msg: error.message }))
            }
        }
    }

    return {
        formik,
        isAddSurveyLoading,
        isAddDemolitionLoading,
    }
}

export default usePocDetails
